import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Table from "./components/ui/Table";
import Pagination from "./components/ui/Pagination";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${baseUrl}/notification/list?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await res.json();

      if (result.data) {
        setNotifications(result.data);
        setTotal(result.total || 0);
      }
    } catch (error) {
      toast.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);


  // Create or update notification
  const handleCreateOrUpdate = async () => {
    if (!title || !message) {
      toast.error("All fields are required");
      return;
    }
    const token = localStorage.getItem("token");
    const url = editId
      ? `${baseUrl}/notification/edit/${editId}`
      : `${baseUrl}/notification/create`;
    const method = editId ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title, message }),
      });

      const result = await res.json();

      if (res.ok) {
        setTitle("");
        setMessage("");
        setEditId(null);
        toast.success(editId ? "Updated successfully!" : "Created successfully!");
        await fetchNotifications();
      } else {
        toast.error(result.message || "Something went wrong");
      }
    } catch (error) {
      toast.error("Server error");
    }
  };

  // send notification
  const handleSend = async (id) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${baseUrl}/notification/send-notification/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();

      if (res.ok) {
        toast.success("Notification sent successfully!");
      } else {
        toast.error(result.message || "Failed to send notification.");
      }
    } catch (error) {
      toast.error("Error sending notification.");
    }
  };


  // delete notification
  const handleDelete = async (id) => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${baseUrl}/notification/delete/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();

      if (res.ok) {
        toast.success("Deleted successfully!");
        await fetchNotifications();
      } else {
        toast.error(result.message || "Failed to delete.");
      }
    } catch {
      toast.error("Error deleting notification.");
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  const columns = [
    { key: "title", header: "Title" },
    { key: "message", header: "Message" },
    {
      key: "actions",
      header: "Actions",
      render: (noti) => (
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              setEditId(noti.id);
              setTitle(noti.title);
              setMessage(noti.message);
            }}
            className="text-blue-400 hover:text-blue-300 transition"
          >
            Edit
          </button>
          <button
            onClick={() => handleSend(noti.id)}
            className="text-emerald-400 hover:text-emerald-300 transition"
          >
            Send
          </button>
          <button
            onClick={() => handleDelete(noti.id)}
            className="text-red-400 hover:text-red-300 transition"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col md:flex-row bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4 text-white">Notifications</h2>

          {/* Create Notification */}
          <div className="bg-gray-800 p-4 rounded shadow mb-6">
            <input
              type="text"
              className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
              placeholder="Message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <button
              className="bg-brand text-white px-4 py-2 rounded hover:bg-brand-dark transition"
              onClick={handleCreateOrUpdate}
            >
              {editId ? "Update" : "Create"}
            </button>
          </div>

          {/* Notification List */}
          <Table
            columns={columns}
            rows={notifications}
            rowKey={(noti) => noti.id}
            loading={loading}
            emptyMessage="No data found."
          />

          <div className="mt-6">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPage;
